import React from 'react';
import { Popover, Divider, Typography } from 'antd';
import { Phone, ChevronDown, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVoiceCreditStatus } from '../../../modules/billing/hooks/useVoiceCreditStatus';

const { Text } = Typography;

/**
 * Header indicator for the voice (SDR talking-agent) add-on (Frente C).
 *
 * MINUTES-first: the pill shows minutos restantes, NOT VC. Colour shifts to
 * amber when low and red when empty (fail-closed). Renders NOTHING when voice
 * is off for the tenant (feature flag off) so the header stays clean.
 */
const VoiceCreditPopover: React.FC = () => {
  const { model, isVoiceEnabled, leituraFalhou } = useVoiceCreditStatus();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Sem leitura a pílula SOME em vez de dizer "sem voz": um cabeçalho não tem
  // onde explicar que a leitura falhou, e o painel de billing já explica. É o
  // comportamento que o erro sempre teve aqui — o que mudou é que uma leitura
  // boa em cache continua valendo quando um poll posterior falha.
  if (!isVoiceEnabled || leituraFalhou) return null;

  // Feature on but no tier contracted — nudge to contract from the popover.
  const pillColor = !model.active
    ? '#64748B'
    : model.isEmpty
      ? '#EF4444'
      : model.isLow
        ? '#D97706'
        : '#334155';

  const pillLabel = model.active
    ? `${model.minutesRemaining.toLocaleString('pt-BR')} ${t('voiceCredits.minutesShort', 'min')}`
    : t('voiceCredits.noPlanShort', 'sem voz');

  const content = (
    <div style={{ width: 280 }}>
      {model.active ? (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 6,
            }}
          >
            <Text strong style={{ fontSize: 14, color: 'var(--color-text-primary)' }}>
              {model.minutesRemaining.toLocaleString('pt-BR')}{' '}
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                {t('voiceCredits.minutesShort', 'min')}{' '}
                {t('voiceCredits.available', 'disponível').toLowerCase()}
              </Text>
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {model.tierLabel}
            </Text>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 13,
                color: '#334155',
              }}
            >
              <span>{t('voiceCredits.minutesIncluded', 'Minutos do ciclo')}</span>
              <span style={{ fontWeight: 600 }}>
                {model.minutesIncluded.toLocaleString('pt-BR')}{' '}
                {t('voiceCredits.minutesShort', 'min')}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 13,
                color: '#334155',
              }}
            >
              <span>{t('voiceCredits.concurrency', 'Concorrência')}</span>
              <span style={{ fontWeight: 600 }}>
                {model.maxConcurrent ?? '—'} {t('voiceCredits.simultaneous', 'simultâneas')}
              </span>
            </div>
          </div>

          {(model.isLow || model.isEmpty) && (
            <>
              <Divider style={{ margin: '12px 0 10px' }} />
              <Text type={model.isEmpty ? 'danger' : 'warning'} style={{ fontSize: 12 }}>
                {model.isEmpty
                  ? t(
                      'voiceCredits.emptyDesc',
                      'Minutos esgotados — recarregue ou faça upgrade para continuar discando.',
                    )
                  : t('voiceCredits.lowShort', 'Minutos acabando — recarregue ou faça upgrade.')}
              </Text>
            </>
          )}
        </>
      ) : (
        <Text style={{ fontSize: 13, color: '#334155' }}>
          {t('voiceCredits.noPlanShortDesc', 'Sem plano de voz contratado.')}
        </Text>
      )}

      <Divider style={{ margin: '10px 0 10px' }} />

      <a
        onClick={() => navigate('/admin/billing')}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          fontSize: 12,
          color: '#6366F1',
          cursor: 'pointer',
          fontWeight: 500,
        }}
      >
        {t('voiceCredits.manage', 'Gerenciar voz')}
        <ArrowUpRight size={12} />
      </a>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      overlayInnerStyle={{ borderRadius: 12, padding: 16 }}
    >
      <button
        className="flex items-center gap-2 rounded-full hover:bg-slate-100 transition-colors cursor-pointer border-0"
        style={{
          background: '#F8FAFC',
          borderRadius: 999,
          padding: '5px 12px',
          border: '1px solid #E2E8F0',
          cursor: 'pointer',
        }}
        aria-label={t('voiceCredits.title', 'Voz (SDR Agente)')}
      >
        <Phone size={14} style={{ color: '#4F46E5' }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: pillColor }}>{pillLabel}</span>
        <ChevronDown size={11} style={{ color: 'var(--color-text-tertiary)' }} />
      </button>
    </Popover>
  );
};

export default VoiceCreditPopover;
